import { useState, useCallback, useRef, useEffect } from 'react';
import ExportService from '../services/ExportService';
import { FILE_TYPES } from '../constants';
import { ExportProfile, ExportResult } from '../types';

type OrderId = string | number;

interface ExportConfig {
  fileType: string;
  selectedProfile: string | null;
  disabled: boolean;
}

const initialExportConfig: ExportConfig = {
  fileType: FILE_TYPES.CSV,
  selectedProfile: null,
  disabled: false
};

interface ExportState extends ExportConfig {
  profiles: ExportProfile[];
  showExportDialog: boolean;
}

export interface UseOrderExportResult {
  exportState: ExportState;
  profiles: ExportProfile[];
  exporting: boolean;
  exportConfig: ExportConfig;
  showExportDialog: () => Promise<void>;
  showExportDialogState: boolean;
  exportError: string | null;
  loadProfiles: () => Promise<ExportProfile[]>;
  hideExportDialog: () => void;
  exportOrders: (orderIds: OrderId[]) => Promise<ExportResult | null>;
  downloadPackingSlips: (orderIds: OrderId[]) => Promise<ExportResult | null>;
  updateExportState: (updates: Partial<ExportConfig>) => void;
  updateExportConfig: (updates: Partial<ExportConfig>) => void;
  clearError: () => void;
}

export function useOrderExport(): UseOrderExportResult {
  const [profiles, setProfiles] = useState<ExportProfile[]>([]);
  const [exporting, setExporting] = useState(false);
  const [exportConfig, setExportConfig] = useState<ExportConfig>(initialExportConfig);
  const [showExportDialogState, setShowExportDialog] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Load export profiles
  const loadProfiles = useCallback(async (): Promise<ExportProfile[]> => {
    try {
      if (profiles.length > 0) {
        return profiles;
      }

      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      const loadedProfiles = await ExportService.loadProfiles(abortControllerRef.current.signal);

      // Only update state if component is still mounted
      if (!isMountedRef.current) return [];

      setProfiles(loadedProfiles);

      // Set default profile if none selected
      if (!exportConfig.selectedProfile && loadedProfiles.length > 0) {
        setExportConfig(prev => ({
          ...prev,
          selectedProfile: loadedProfiles[0].Name
        }));
      }

      return loadedProfiles;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return [];
      }
      console.error('Error loading profiles:', error);
      if (isMountedRef.current) {
        setExportError(error.message);
      }
      return [];
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles.length, exportConfig.selectedProfile]);

  // Show export dialog
  const showExport = useCallback(async () => {
    await loadProfiles();
    setShowExportDialog(true);
  }, [loadProfiles]);

  // Hide export dialog
  const hideExportDialog = useCallback(() => {
    setShowExportDialog(false);
  }, []);

  // Export orders
  const exportOrders = useCallback(async (orderIds: OrderId[]): Promise<ExportResult | null> => {
    try {
      setExporting(true);
      setExportConfig(prev => ({ ...prev, disabled: true }));

      const profile = profiles?.find(
        p => p.Name === exportConfig.selectedProfile
      );

      if (!profile) {
        throw new Error('Please select an export profile');
      }

      // Create abort controller for this export
      const exportAbortController = new AbortController();

      const result = await ExportService.exportOrders(
        orderIds,
        profile,
        exportConfig.fileType,
        exportAbortController.signal
      );

      if (isMountedRef.current) {
        setExportConfig(prev => ({ ...prev, disabled: false }));
      }
      return result;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return null;
      }
      console.error('Error exporting orders:', error);
      if (isMountedRef.current) {
        setExportError(error.message);
        setExportConfig(prev => ({ ...prev, disabled: false }));
      }
      throw error;
    } finally {
      if (isMountedRef.current) {
        setExporting(false);
      }
    }
  }, [exportConfig.selectedProfile, exportConfig.fileType, profiles]);

  // Download packing slips
  const downloadPackingSlips = useCallback(async (orderIds: OrderId[]): Promise<ExportResult | null> => {
    try {
      setExporting(true);
      const downloadAbortController = new AbortController();
      const result = await ExportService.downloadPackingSlips(orderIds, downloadAbortController.signal);
      return result;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return null;
      }
      console.error('Error downloading packing slips:', error);
      if (isMountedRef.current) {
        setExportError(error.message);
      }
      throw error;
    } finally {
      if (isMountedRef.current) {
        setExporting(false);
      }
    }
  }, []);

  // Update export configuration
  const updateExportConfig = useCallback((updates: Partial<ExportConfig>) => {
    setExportConfig(prev => ({ ...prev, ...updates }));
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setExportError(null);
  }, []);

  return {
    exportState: {
      ...exportConfig,
      profiles,
      disabled: exporting,
      showExportDialog: showExportDialogState
    },
    profiles,
    exporting,
    exportConfig,
    showExportDialog: showExport,
    showExportDialogState,
    exportError,
    loadProfiles,
    hideExportDialog,
    exportOrders,
    downloadPackingSlips,
    updateExportState: updateExportConfig,
    updateExportConfig,
    clearError
  };
}